/**
 * Section Quarantine Banner (SNT-3).
 *
 * Defense-in-depth visual indicator for sections where the AI emitted
 * instruction-shape text and the SNT-1 guard swapped in a data-grounded
 * fallback (SNT-2). Renders a theme-toned WARNING banner ABOVE the section
 * content (the warning role — was hardcoded Tailwind amber literals that
 * clashed on non-default presets and read off-theme on the default;
 * retoned via roleColor(theme,'warning') per QA 2026-06-10) with three
 * affordances:
 *   - "Author" CTA — triggers the existing UAE-2 authored-claim dialog
 *   - "Re-run section" CTA — calls the section regeneration handler
 *   - "Show original AI output" disclosure — collapses the quarantined
 *     instruction-shape text for transparency / audit
 *
 * Driven by:
 *   - `report.sectionGuardStatus[sectionId] === 'degraded-instruction-leak'`
 *   - `report.quarantinedOriginal[sectionId]` (collapsed under disclosure)
 *
 * Also supports a CLIENT-side detection mode (SNT-3 defense-in-depth):
 * when the gen-time guard MISSED a leak but the regex fires at render
 * time, the banner shows with the alternate "Detected at render"
 * copy and no original-output disclosure (we don't have one).
 *
 * REPORT PAGE (2026-10-13, William: no banners on report pages): renders
 * nothing where `useInSectionNoticesVisible()` is false — the interactive
 * report page. It still renders in the delivered PDF / print tree, unchanged.
 * On the page, the header status chip names the section and ⋮ "Section
 * quality notes" offers the same Author / Re-run section / original-output
 * affordances (`reportSectionNotices.ts`, `SectionNoticesDialog`).
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useInSectionNoticesVisible } from './inSectionNotices';
import { detectInstructionShape } from '../../../lib/instructionShapeGuard';
import { DEFAULT_THEME, type ReportTheme } from '../../../types/reportTheme';
import { roleColor, roleColorAlpha } from '../../../lib/reportRoleColors';

export interface SectionQuarantineBannerProps {
  /** Field path key in `report.sectionGuardStatus` / `quarantinedOriginal`. */
  sectionId: string;
  /** Human-readable section name for the banner copy. */
  sectionLabel: string;
  /** The gen-time guard status. `undefined` means run client-side check. */
  guardStatus?: 'degraded-instruction-leak' | 'fallback-data-only';
  /** Original (quarantined) AI output — shown under the disclosure. */
  quarantinedOriginal?: string;
  /** The CURRENTLY rendered text — checked client-side as a fallback
   * if the gen-time guard missed an instruction-shape pattern. */
  renderedText?: string;
  /** Click handler for "Author" CTA — opens the UAE-2 authored-claim
   * dialog scoped to this section. */
  onAuthor?: (sectionId: string) => void;
  /** Click handler for "Re-run section" CTA. */
  onRerun?: (sectionId: string) => void;
  /**
   * Active report theme — drives the banner's warning chrome so it follows
   * the active preset instead of the fixed Tailwind amber literals that
   * clashed on non-default themes (and read as off-theme orange on the
   * default). Optional: omitting it falls through to DEFAULT_THEME so the
   * existing (theme-less) call sites keep a consistent look. The banner can
   * appear inside the export/print tree, so the colours are resolved to
   * inline hex via roleColor (the CSS-var island doesn't reach inline style).
   */
  theme?: ReportTheme;
}

export const SectionQuarantineBanner: React.FC<SectionQuarantineBannerProps> = ({
  sectionId,
  sectionLabel,
  guardStatus,
  quarantinedOriginal,
  renderedText,
  onAuthor,
  onRerun,
  theme = DEFAULT_THEME,
}) => {
  const visibleHere = useInSectionNoticesVisible();
  const isQuarantinedAtGen = guardStatus === 'degraded-instruction-leak';

  // Client-side check fires only when the gen-time guard didn't already
  // flag this section AND the rendered text matches the instruction-
  // shape regex. This is the defense-in-depth path for leaks that
  // bypassed the genServer guard.
  const clientCheck = !isQuarantinedAtGen
    ? detectInstructionShape(renderedText)
    : { flagged: false };

  if (!visibleHere) return null;
  if (!isQuarantinedAtGen && !clientCheck.flagged) return null;

  // Theme-driven warning chrome — replaces the literal Tailwind amber
  // (border-amber-300 / bg-amber-50 / text-amber-700|800|900) so the banner
  // follows the active preset. Resolved to inline hex because the banner can
  // render inside the export/print tree where the CSS-var island isn't in
  // scope. The quarantined-original body stays neutral 'muted' ink.
  const warning = roleColor(theme, 'warning');
  const warningBorder = roleColorAlpha(theme, 'warning', 0.42);
  const warningWash = roleColorAlpha(theme, 'warning', 0.08);
  const mutedInk = roleColor(theme, 'muted');

  return (
    <div
      data-testid="section-quarantine-banner"
      data-section-id={sectionId}
      data-mode={isQuarantinedAtGen ? 'gen-time' : 'render-time'}
      className="rounded-md border p-3 mb-2 space-y-2"
      style={{ borderColor: warningBorder, background: warningWash }}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: warning }} aria-hidden />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium" style={{ color: warning }}>
            AI-generated section guidance, not findings
          </p>
          <p className="text-xs leading-relaxed" style={{ color: warning }}>
            {isQuarantinedAtGen
              ? `This ${sectionLabel} narrative read as instruction-shape rather than grounded findings. The AI may not have had enough evidence. Click `
              : `This ${sectionLabel} text was flagged at render time as possibly instruction-shape. Consider re-running. `}
            {(onAuthor || onRerun) && isQuarantinedAtGen ? (
              <>
                <strong>Author</strong> to provide your own narrative, or <strong>Re-run section</strong> to retry generation.
              </>
            ) : null}
          </p>
        </div>
      </div>

      {(onAuthor || onRerun) ? (
        <div className="flex gap-2 pl-6">
          {onAuthor ? (
            <button
              type="button"
              onClick={() => onAuthor(sectionId)}
              className="text-xs font-bold uppercase tracking-widest underline"
              style={{ color: warning }}
            >
              Author
            </button>
          ) : null}
          {onRerun ? (
            <button
              type="button"
              onClick={() => onRerun(sectionId)}
              className="text-xs font-bold uppercase tracking-widest underline"
              style={{ color: warning }}
            >
              Re-run section
            </button>
          ) : null}
        </div>
      ) : null}

      {isQuarantinedAtGen && quarantinedOriginal ? (
        <details className="pl-6 text-xs" style={{ color: warning }}>
          <summary className="cursor-pointer">Show original AI output</summary>
          <p className="mt-1 italic whitespace-pre-wrap" style={{ color: mutedInk }}>{quarantinedOriginal}</p>
        </details>
      ) : null}
    </div>
  );
};
